import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook for handling unsaved changes - handles both browser and React Router navigation
 * @param {boolean} hasUnsavedChanges - Whether there are unsaved changes
 * @param {Object} options - Configuration options
 * @returns {Object} - Hook utilities
 */
const useUnsavedChangesSimple = (hasUnsavedChanges, options = {}) => {
  const {
    message = 'You have unsaved changes that will be lost if you leave this page.',
    enableBrowserWarning = true,
  } = options;

  const navigate = useNavigate();
  const location = useLocation();

  const [showModal, setShowModal] = useState(false);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [isBackNavigation, setIsBackNavigation] = useState(false);

  // Handle browser beforeunload event (page refresh/close)
  useEffect(() => {
    if (!enableBrowserWarning) return;

    const handleBeforeUnload = event => {
      if (hasUnsavedChanges && !confirmedNavigation) {
        event.preventDefault();
        event.returnValue = message; // Standard browser message
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, confirmedNavigation, enableBrowserWarning, message]);

  // Handle browser back/forward buttons
  useEffect(() => {
    if (!hasUnsavedChanges || confirmedNavigation) return;

    const handlePopState = event => {
      if (hasUnsavedChanges && !confirmedNavigation) {
        // Prevent navigation by pushing current state back
        window.history.pushState(null, '', window.location.pathname);

        // Mark this as back navigation and show modal
        setIsBackNavigation(true);
        setShowModal(true);
      }
    };

    // Add a dummy state to catch back button
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, confirmedNavigation]);

  // Handle React Router navigation interception by wrapping history.pushState
  useEffect(() => {
    if (!hasUnsavedChanges || confirmedNavigation) return;

    // Store the original pushState function
    const originalPushState = window.history.pushState;

    // Create a wrapper that intercepts pushState calls
    const interceptedPushState = function (state, title, url) {
      // Check if this is a navigation we should block (and not our own dummy state)
      if (url && url !== window.location.pathname && hasUnsavedChanges && !confirmedNavigation) {
        // Store where we were trying to go
        setPendingLocation({ pathname: url });
        setShowModal(true);

        return; // Don't navigate
      }

      // If no unsaved changes or navigation is confirmed, proceed
      return originalPushState.call(this, state, title, url);
    };

    window.history.pushState = interceptedPushState;

    return () => {
      window.history.pushState = originalPushState;
    };
  }, [hasUnsavedChanges, confirmedNavigation]);

  // Handle modal actions
  const handleKeepEditing = useCallback(() => {
    setShowModal(false);
    setPendingLocation(null);
    setIsBackNavigation(false);
  }, []);

  const handleDiscardChanges = useCallback(() => {
    setConfirmedNavigation(true);
    setShowModal(false);

    // If we have a pending location (React Router navigation), go there
    if (pendingLocation) {
      setTimeout(() => {
        navigate(pendingLocation.pathname);
        setPendingLocation(null);
      }, 100);
    } else if (isBackNavigation) {
      // For browser back navigation, go back twice:
      // once to undo our preventive pushState, then actually go back
      setTimeout(() => {
        window.history.go(-2);
        setIsBackNavigation(false);
      }, 100);
    } else {
      // Fallback for other cases
      setTimeout(() => {
        window.history.back();
      }, 100);
    }
  }, [navigate, pendingLocation, isBackNavigation]);

  // Reset when navigation is confirmed
  useEffect(() => {
    if (confirmedNavigation) {
      const timer = setTimeout(() => {
        setConfirmedNavigation(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [confirmedNavigation]);

  // Reset confirmed navigation when location changes
  useEffect(() => {
    setConfirmedNavigation(false);
  }, [location.pathname]);

  return {
    // Modal state and handlers
    showModal,
    handleKeepEditing,
    handleDiscardChanges,

    // Current state
    hasUnsavedChanges,
    pendingLocation,
  };
};

export default useUnsavedChangesSimple;
